import {State, Module} from 'app/decorators';
import Activity from 'app/directives/activity';
import Modal from 'app/services/modals';
import Config from 'app/services/config';
import BaseRoute from 'app/routing';
import FileStore from 'app/stores/file';
import TicketStore from 'app/stores/ticket';
import ProductConfStore from 'app/stores/product.conf';
import isActiveDirective from 'app/directives/is.active';
import {ListCtrl, DetailsCtrl, TicketDetailsCtrl, FilesCtrl, TicketLogCtrl, TicketShippingCtrl, TicketNotesCtrl} from './ctrls';

//components
import Formula from './components/formula';
import Presets from './components/presets';
import ProductPicker from 'app/directives/product.picker';
import ProductsComponent from 'app/directives/products.component';
import TicketStatusComponent from 'app/directives/ticket.status';
import Blink from 'app/directives/blink';
import CompanyPicker from 'app/directives/company.picker';
import BillingComponent from 'app/directives/billing/component';
import TicketList from './components/list';
import TankPicker from 'app/directives/tank.picker';
import StatusButton from 'app/directives/status.button/component';
import PrintButton from 'app/directives/print.button/component';
import TicketPriorityList from './components/priority/main';
import ChemicalProgramComponent from 'app/directives/chemical.program.picker';
import SalesCodesPicker from 'app/directives/sales.code.picker';
import AnalysisWarningComponent from 'app/directives/analysis_warning';
import AcresControlComponent from 'app/directives/acres.calc.control';
import ProductCodePicker from 'app/directives/product_code.picker';

import FadeOut from 'app/directives/fade.out';

// Templates
import listTpl from './templates/list.html';
import detailTpl from './templates/detail.html';
import ticketDetailTpl from './templates/detail.ticket.html';
import ticketLogTpl from './templates/detail.log.html';
import actionsTpl from './templates/actions.html';
import filesTpl from "./templates/detail.files.html";
import shippingTpl from "./templates/detail.shipping.html";
import notesTpl from "./templates/detail.notes.html";
@Module({
    name: 'app.tickets',
    modules: [
        BaseRoute,
        Modal,
        Config,
        TicketStore,
        ProductConfStore,
        ProductPicker,
        ProductsComponent,
        TicketStatusComponent,
        TicketPriorityList,
        ChemicalProgramComponent,
        Blink,
        FadeOut,
        Formula,
        Presets,
        CompanyPicker,
        TicketList,
        BillingComponent,
        TankPicker,
        Activity,
        StatusButton,
        PrintButton,
        isActiveDirective,
        SalesCodesPicker,
        AnalysisWarningComponent,
        AcresControlComponent,
        ProductCodePicker,
        FileStore,
    ],
})
@State({
    name: 'r.in.top.tickets',
    url: '/tickets',
    views: {
        '': {
            template: '<ui-view></ui-view>',
        },
        actions: {
            template: actionsTpl,
        }
    },
    abstract: '.list',
    ncyBreadcrumb: {
        label: 'Tickets',
    },
})
@State({
    name: 'r.in.top.tickets.list',
    abstract: '.main',
    controller: ListCtrl,
    template: listTpl,
    ncyBreadcrumb: {
        skip: true,
    },
})
@State({
    name: 'r.in.top.tickets.list.main',
    url: '?company&search&created_at__range&status&page&ordering&contains_product',
    // The React page owns these query params (it writes them directly and
    // refetches itself); the legacy fallback also drives them via UI + an
    // explicit `$state.go('.', ..., {notify: false})`. Neither relies on the
    // state reloading when they change, so disable it - otherwise Angular
    // remounts the React island (and reconciles a stale `$location`) on every
    // paginate/filter, causing flicker and the "click navbar twice" bug.
    reloadOnSearch: false,
    // Feature flag managed by the backend controls the React list rollout.
    templateProvider: function (Config) {
        "ngInject";
        const useReact = Config.isFeatureFlagActive('REACT_TICKETS_LIST');
        return useReact
            ? `<div react-view="tickets-list"></div>`
            : `<ticket-list extra-fields="['customer']"></ticket-list>`;
    },
    ncyBreadcrumb: {
        skip: true,
    },
})
@State({
    name: 'r.in.top.tickets.list.priority',
    url: '/sorted',
    template: '<ticket-priority-list></ticket-priority-list>',
    data: {
        permissions: {
            only: [
                'main.can_schedule_tickets',
            ],
        },
    },
    ncyBreadcrumb: {
        skip: true,
    },
})
@State({
    name: 'r.in.top.tickets.map',
    url: '/map?status&company&delivery&crop&locations&contains_product&applicator',
    // The React page owns these query params, see `r.in.top.tickets.list.main`.
    reloadOnSearch: false,
    template: '<div react-view="tickets-map"></div>',
    ncyBreadcrumb: {
        label: 'Ticket Map',
    },
})
@State({
    name: 'r.in.top.tickets.detail',
    url: '/{ticketId:[0-9-]+}',
    controllerProvider: function (Config) {
        "ngInject";
        const useReact = Config.isFeatureFlagActive('REACT_TICKET_DETAIL');
        return useReact
            ? function ($stateParams, $ticket) {
                "ngInject";
                this.ticket = $ticket;
                this.autoEdit = !!$stateParams.created;
            }
            : DetailsCtrl;
    },
    templateProvider: function (Config) {
        "ngInject";
        const useReact = Config.isFeatureFlagActive('REACT_TICKET_DETAIL');
        return useReact
            ? '<div react-view="ticket-detail" props="{ticket: vmo.ticket, autoEdit: vmo.autoEdit}"></div><div ui-view style="display:none"></div>'
            : detailTpl;
    },
    as: 'vmo',
    resolve: {
        $ticket: function($stateParams, TicketStore, Config, ReactBridge, $q) {
            if (Config.isFeatureFlagActive('REACT_TICKET_DETAIL')) {
                return $q.when(ReactBridge().getResourceStore('tickets').getState().get($stateParams.ticketId));
            }
            return TicketStore.get($stateParams.ticketId);
        },
        $prodConf: function(ProductConfStore, $ticket, Config) {
            if (Config.isFeatureFlagActive('REACT_TICKET_DETAIL')) { return null; }
            ProductConfStore.params.company = $ticket.company.id;
            return ProductConfStore.refresh();
        },
        $company: function(CompanyStore, $ticket, Config) {
            if (Config.isFeatureFlagActive('REACT_TICKET_DETAIL')) { return null; }
            return CompanyStore.get($ticket.company.id);
        }
    },
    ncyBreadcrumb: {
        label: 'Ticket #{{ vmo.ticket.ticketnumber }}',
        parent: 'r.in.top.tickets.list',
    },
    params: {
        created: null,
    },
    abstract: '.ticket',
})
@State({
    name: 'r.in.top.tickets.detail.ticket',
    url: '',
    controllerProvider: function (Config) {
        "ngInject";
        const useReact = Config.isFeatureFlagActive('REACT_TICKET_DETAIL');
        return useReact
            ? function () {
                "ngInject";
            }
            : TicketDetailsCtrl;
    },
    templateProvider: function (Config) {
        "ngInject";
        const useReact = Config.isFeatureFlagActive('REACT_TICKET_DETAIL');
        return useReact
            ? '<div style="display:none"></div>'
            : ticketDetailTpl;
    },
    ncyBreadcrumb: {
        label: 'Details',
    },
})
@State({
    name: 'r.in.top.tickets.detail.files',
    url: '/files',
    controllerProvider: function (Config) {
        "ngInject";
        const useReact = Config.isFeatureFlagActive('REACT_TICKET_DETAIL');
        return useReact
            ? function () {
                "ngInject";
            }
            : FilesCtrl;
    },
    templateProvider: function (Config) {
        "ngInject";
        const useReact = Config.isFeatureFlagActive('REACT_TICKET_DETAIL');
        return useReact
            ? '<div style="display:none"></div>'
            : filesTpl;
    },
    ncyBreadcrumb: {
        label: 'Files',
    },
})
@State({
    name: 'r.in.top.tickets.detail.shipping',
    url: '/shipping',
    controllerProvider: function (Config) {
        "ngInject";
        const useReact = Config.isFeatureFlagActive('REACT_TICKET_DETAIL');
        return useReact
            ? function () {
                "ngInject";
            }
            : TicketShippingCtrl;
    },
    templateProvider: function (Config) {
        "ngInject";
        const useReact = Config.isFeatureFlagActive('REACT_TICKET_DETAIL');
        return useReact
            ? '<div style="display:none"></div>'
            : shippingTpl;
    },
    ncyBreadcrumb: {
        label: 'Shipping',
    },
})
@State({
    name: 'r.in.top.tickets.detail.notes',
    url: '/notes',
    controllerProvider: function (Config) {
        "ngInject";
        const useReact = Config.isFeatureFlagActive('REACT_TICKET_DETAIL');
        return useReact
            ? function () {
                "ngInject";
            }
            : TicketNotesCtrl;
    },
    templateProvider: function (Config) {
        "ngInject";
        const useReact = Config.isFeatureFlagActive('REACT_TICKET_DETAIL');
        return useReact
            ? '<div style="display:none"></div>'
            : notesTpl;
    },
    ncyBreadcrumb: {
        label: 'Notes',
    },
})
@State({
    name: 'r.in.top.tickets.detail.log',
    url: '/log',
    controllerProvider: function (Config) {
        "ngInject";
        const useReact = Config.isFeatureFlagActive('REACT_TICKET_DETAIL');
        return useReact
            ? function () {
                "ngInject";
            }
            : TicketLogCtrl;
    },
    templateProvider: function (Config) {
        "ngInject";
        const useReact = Config.isFeatureFlagActive('REACT_TICKET_DETAIL');
        return useReact
            ? '<div style="display:none"></div>'
            : ticketLogTpl;
    },
    ncyBreadcrumb: {
        label: 'Log',
    },
})

export default class States { }



// WEBPACK FOOTER //
// ./src/app/tickets/routing.js